import React, { useState } from "react";
import {
  isTerminalStatus,
  isKnownImportStatus,
  STATUS_LABELS,
  statusPillClass,
} from "../../../lib/importStatus";
import { useImportProgress } from "../hooks/useImportProgress";
import { Loader2, StopCircle } from "lucide-react";
import { ProgressPanel } from "./step5/ProgressPanel";
import { TerminalSummary } from "./step5/TerminalSummary";
import { ActionRow } from "./step5/ActionRow";
import { CancelConfirmDialog } from "./step5/CancelConfirmDialog";
import { RetryConfirmDialog } from "./step5/RetryConfirmDialog";

type JobShape = {
  id: string;
  status: string;
  version: number;
  counts?: Record<string, number>;
  cancel_requested_at?: string | null;
};

/**
 * Step 5 — commit/cancel/retry/final-summary owner.
 *
 * Renders one of three surfaces based on real backend status:
 *   COMMITTING / CANCELLING  → live progress (polls /progress)
 *   COMPLETED / PARTIAL / FAILED / CANCELLED → terminal summary (fetches /summary once)
 *   any other pre-terminal (RECEIVED..VALIDATED) → never routed here by the shell.
 *
 * This owner deliberately keeps:
 *   - polling / reconnect state (via `useImportProgress`)
 *   - canonical job state (from props → shell)
 *   - dialog open/close flags
 *   - counter derivation from server truth
 *
 * All presentational and dialog concerns are delegated to `./step5/*`.
 */
export function Step5Progress({
  job,
  onJobUpdated,
}: {
  job: JobShape;
  onJobUpdated: () => void;
}) {
  const { progress, reconnecting } = useImportProgress(job.id, job.status, onJobUpdated);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);

  const isRunning = job.status === "COMMITTING" || job.status === "CANCELLING";
  const isTerminal = isTerminalStatus(job.status);

  const counts = progress?.counts ?? job.counts ?? {};
  const total = Number(counts.total ?? 0);
  const created = Number(counts.created ?? 0);
  const updated = Number(counts.updated ?? 0);
  const skipped = Number(counts.skipped ?? 0);
  const failed = Number(counts.failed ?? 0);
  const processed = created + updated + skipped + failed;
  const remaining = total > 0 ? Math.max(0, total - processed) : null;
  const percent =
    total > 0 && processed >= 0 && processed <= total
      ? Math.floor((processed / total) * 100)
      : null;

  return (
    <div className="space-y-6" data-testid="step-5-progress">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusPillClass(job.status)}`}
          data-testid="step-5-status-pill"
        >
          {isKnownImportStatus(job.status) ? STATUS_LABELS[job.status] : job.status}
        </span>
        <h2 className="text-lg font-semibold text-slate-900">
          {isTerminal ? "Import result" : "Import in progress"}
        </h2>
      </div>

      {job.status === "CANCELLING" && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900"
          data-testid="cancelling-banner"
        >
          <StopCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Cancellation requested. The worker is safely finishing the currently claimed
            row and will not claim another row.
          </div>
        </div>
      )}

      {reconnecting && !isTerminal && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700"
          data-testid="reconnecting-hint"
        >
          <Loader2 className="w-3.5 h-3.5 mt-0.5 animate-spin motion-reduce:animate-none shrink-0" />
          Reconnecting to the import worker…
        </div>
      )}

      {isRunning && (
        <ProgressPanel
          percent={percent}
          processed={processed}
          total={total}
          remaining={remaining}
          created={created}
          updated={updated}
          skipped={skipped}
          failed={failed}
          heartbeatAt={progress?.heartbeat_at ?? null}
          inProgress={!!progress?.in_progress}
        />
      )}

      {isTerminal && <TerminalSummary jobId={job.id} status={job.status} />}

      <ActionRow
        job={job}
        onCancelClick={() => setCancelOpen(true)}
        onRetryClick={() => setRetryOpen(true)}
      />

      {cancelOpen && (
        <CancelConfirmDialog
          job={job}
          open
          onClose={() => setCancelOpen(false)}
          onCancelled={onJobUpdated}
        />
      )}
      {retryOpen && (
        <RetryConfirmDialog
          job={job}
          open
          onClose={() => setRetryOpen(false)}
          onRetried={onJobUpdated}
        />
      )}
    </div>
  );
}
